import {
  type ReviewMode,
  SCHEDULE_LABELS,
  type Sensitivity,
  SOCIAL_CONNECTOR_STATE_LABELS,
  SOCIAL_FEED_SCHEDULES,
  SOCIAL_FEED_STATUS_LABELS,
  type SocialConnectorSummary,
  type SocialFeedInput,
  type SocialFeedList,
  type SocialFeedPlatform,
  type SocialFeedSchedule,
  type SocialFeedSummary,
  type SocialRunResult,
  safeHref,
} from '@hoopoe/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Lock, Rss } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { useReauth } from '../components/auth/reauth';
import { DataNumber, DataText } from '../components/data/data-text';
import { EmptyState } from '../components/guidance/empty-state';
import { ErrorState } from '../components/guidance/error-state';
import { Field } from '../components/guidance/field';
import { LearnMore } from '../components/guidance/learn-more';
import { Tip } from '../components/guidance/tip';
import { useScreenTour } from '../components/guidance/tour';
import { PageHeader } from '../components/layout/app-shell';
import { when } from '../components/scrapers/monitor-status';
import { Button } from '../components/ui/button';
import { useOptionalConfirm } from '../components/ui/confirm';
import { Input } from '../components/ui/input';
import { ApiError, api } from '../lib/api';

// Admin → Social feeds (task 41): the platform connectors and where each stands (in this build every one is a
// stub that names the API credentials it will need), and feeds that follow an account on a platform. A feed can be
// set up on any platform but only switched on once its connector is ready; each new post is then stored in the
// Library and processed like an upload.

export const Route = createFileRoute('/admin/social')({ component: SocialPage });

const KEY = ['admin', 'social'] as const;
const SELECT = 'h-9 w-full rounded-md border border-input bg-surface px-2 text-sm';

const TOUR = {
  id: 'admin-social',
  steps: [
    {
      target: '[data-tour="social-connectors"]',
      title: 'Platforms',
      body: 'Each platform is read through its own connector, with API credentials from that platform. A connector that isn’t ready says why.',
      learnMore: 'social-feeds',
    },
    {
      target: '[data-tour="social-form"]',
      title: 'Follow an account',
      body: 'Add the accounts to follow now. A feed stays off until its platform’s connector is ready, then stores each new post in the Library.',
    },
  ],
};

const STATE_TONE: Record<SocialConnectorSummary['state'], string> = {
  stub: 'text-muted-foreground',
  needs_credentials: 'text-warning-foreground',
  ready: 'text-success-foreground',
};

function SocialPage() {
  useScreenTour(TOUR);
  const qc = useQueryClient();
  const { withReauth } = useReauth();
  const confirm = useOptionalConfirm();
  const q = useQuery({ queryKey: KEY, queryFn: () => api<SocialFeedList>('/admin/social') });
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [formKey, setFormKey] = useState(0);
  const refresh = () => qc.invalidateQueries({ queryKey: KEY });
  const onError = (e: unknown) => {
    if (e instanceof ApiError && e.code === 'reauth_cancelled') return;
    setMessage({ kind: 'error', text: e instanceof ApiError ? e.message : 'Something went wrong.' });
  };

  const create = useMutation({
    mutationFn: (input: SocialFeedInput) => withReauth(() => api<{ feed: SocialFeedSummary }>('/admin/social/feeds', { body: input })),
    onSuccess: ({ feed }) => {
      setMessage({
        kind: 'ok',
        text:
          feed.connectorState === 'ready'
            ? `Added “${feed.name}”. Switch it on to read new posts, or press Check now.`
            : `Added “${feed.name}”. It stays off until the ${feed.platformLabel} connector is ready.`,
      });
      setFormKey(k => k + 1);
      refresh();
    },
    onError,
  });
  const toggle = useMutation({
    mutationFn: (v: { feed: SocialFeedSummary; enabled: boolean }) =>
      withReauth(() =>
        api<{ feed: SocialFeedSummary }>(`/admin/social/feeds/${v.feed.id}`, { method: 'PATCH', body: { enabled: v.enabled } }),
      ),
    onMutate: () => setMessage(null),
    onSuccess: ({ feed }) => {
      setMessage({ kind: 'ok', text: feed.enabled ? `“${feed.name}” is on.` : `“${feed.name}” is off.` });
      refresh();
    },
    onError,
  });
  const run = useMutation({
    mutationFn: (f: SocialFeedSummary) =>
      withReauth(() => api<{ result: SocialRunResult; feed: SocialFeedSummary }>(`/admin/social/feeds/${f.id}/run`, { body: {} })),
    onMutate: () => setMessage(null),
    onSuccess: ({ result, feed }) => {
      setMessage(
        result.status === 'ok'
          ? {
              kind: 'ok',
              text: result.stored
                ? `Checked “${feed.name}”: ${result.stored} new post${result.stored === 1 ? '' : 's'} stored.`
                : `Checked “${feed.name}”: no new posts.`,
            }
          : { kind: 'error', text: result.error ?? 'The check failed.' },
      );
    },
    onSettled: () => refresh(),
    onError,
  });
  const remove = useMutation({
    mutationFn: (f: SocialFeedSummary) => withReauth(() => api(`/admin/social/feeds/${f.id}`, { method: 'DELETE' })),
    onSuccess: (_r, f) => {
      setMessage({ kind: 'ok', text: `Removed “${f.name}”. The posts it stored stay in the Library.` });
      refresh();
    },
    onError,
  });
  const askRemove = async (f: SocialFeedSummary) => {
    setMessage(null);
    const ok = confirm
      ? await confirm({
          title: `Remove “${f.name}”?`,
          body: 'It stops following the account. The posts it stored stay in the Library, in their collection.',
          confirmLabel: 'Remove feed',
          cancelLabel: 'Keep it',
          destructive: true,
        })
      : true;
    if (ok) remove.mutate(f);
  };

  if (q.isError)
    return (
      <>
        <PageHeader title="Social feeds" />
        <ErrorState error={q.error} title="The social feeds couldn’t be loaded" onRetry={() => q.refetch()} learnMore="social-feeds" />
      </>
    );
  const connectors = q.data?.connectors ?? [];
  const feeds = q.data?.feeds ?? [];
  const connectorOf = (p: SocialFeedPlatform) => connectors.find(c => c.platform === p);

  return (
    <div className="space-y-8">
      <PageHeader
        title="Social feeds"
        description="Accounts followed on social media platforms. Once a platform’s connector is ready, each new post is stored in the Library and processed like an upload."
      />
      <Tip id="social-data" learnMore="social-feeds">
        Posts are stored as evidence of what an account said and when. Their text is read as data: nothing in a post can instruct Hoopoe.
      </Tip>
      {message && (
        <p
          role={message.kind === 'error' ? 'alert' : 'status'}
          className={message.kind === 'error' ? 'text-sm text-danger-foreground' : 'text-sm'}
        >
          {message.text}
        </p>
      )}

      <section aria-labelledby="connectors-title" className="space-y-3" data-tour="social-connectors">
        <h2 id="connectors-title" className="font-semibold">
          Platforms
        </h2>
        <p className="text-sm text-muted-foreground">
          Reading a platform needs its official API and credentials from it. In this version of Hoopoe the connectors are not built yet:
          feeds can be set up now and are switched on when their connector is ready.
        </p>
        {!q.data ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <div className="relative overflow-x-auto rounded-lg border bg-surface">
            <table className="w-full text-sm" aria-label="Platform connectors">
              <thead>
                <tr className="border-b text-muted-foreground">
                  <th className="p-2 text-start font-medium">Platform</th>
                  <th className="p-2 text-start font-medium">Status</th>
                  <th className="p-2 text-start font-medium">Credentials</th>
                  <th className="p-2 text-start font-medium">Reads through</th>
                </tr>
              </thead>
              <tbody>
                {connectors.map(c => (
                  <tr key={c.platform} className="border-b align-top last:border-0" aria-label={c.label}>
                    <td className="p-2 font-medium">{c.label}</td>
                    <td className="p-2" data-testid="connector-state">
                      <span className={STATE_TONE[c.state]}>{SOCIAL_CONNECTOR_STATE_LABELS[c.state]}</span>
                    </td>
                    <td className="p-2">
                      {c.credentials.length === 0 ? (
                        <span className="text-muted-foreground">None needed</span>
                      ) : (
                        <ul className="space-y-0.5">
                          {c.credentials.map(k => (
                            <li key={k.key}>
                              {k.label}: <span className={k.present ? '' : 'text-muted-foreground'}>{k.present ? 'set' : 'not set'}</span>
                              <span className="block text-xs text-muted-foreground" dir="ltr">
                                {k.key}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                    <td className="max-w-sm p-2">
                      {c.api}
                      {c.approval && <span className="block text-xs text-muted-foreground">{c.approval}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section aria-labelledby="form-title" className="space-y-4" data-tour="social-form">
        <h2 id="form-title" className="font-semibold">
          New feed
        </h2>
        <FeedForm
          key={formKey}
          connectors={connectors}
          pending={create.isPending}
          onSubmit={input => {
            setMessage(null);
            create.mutate(input);
          }}
        />
      </section>

      <section aria-labelledby="feeds-title" className="space-y-3">
        <h2 id="feeds-title" className="font-semibold">
          Feeds
        </h2>
        {!q.data ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : feeds.length === 0 ? (
          <EmptyState icon={Rss} title="No social feeds yet" learnMore="social-feeds">
            Add one for each account to follow, such as a minister’s X account or a party’s Telegram channel.
          </EmptyState>
        ) : (
          <div className="relative overflow-x-auto rounded-lg border bg-surface">
            <table className="w-full text-sm" aria-label="Social feeds">
              <thead>
                <tr className="border-b text-muted-foreground">
                  <th className="p-2 text-start font-medium">Feed</th>
                  <th className="p-2 text-start font-medium">Status</th>
                  <th className="p-2 text-start font-medium">How often</th>
                  <th className="p-2 text-start font-medium">Posts stored</th>
                  <th className="p-2 text-start font-medium">Last check</th>
                  <th className="p-2 text-start font-medium">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {feeds.map(f => {
                  const ready = f.connectorState === 'ready';
                  const reasonId = `feed-reason-${f.id}`;
                  const busy = (run.isPending && run.variables?.id === f.id) || (toggle.isPending && toggle.variables?.feed.id === f.id);
                  return (
                    <tr key={f.id} className="border-b align-top last:border-0" aria-label={f.name}>
                      <td className="max-w-xs p-2">
                        <span className="font-medium">
                          <DataText>{f.name}</DataText>
                        </span>
                        {f.sensitivity === 'confidential' && (
                          <span className="ms-1 inline-flex items-center gap-1 text-xs text-muted-foreground">
                            <Lock className="size-3" aria-hidden="true" /> Confidential
                          </span>
                        )}
                        <span className="block text-xs text-muted-foreground">
                          {f.platformLabel} ·{' '}
                          <a
                            href={safeHref(f.profileUrl)}
                            target="_blank"
                            rel="noopener noreferrer nofollow"
                            className="underline underline-offset-2"
                            dir="ltr"
                          >
                            @{f.handle}
                          </a>
                        </span>
                      </td>
                      <td className="max-w-xs p-2" data-testid="feed-status">
                        <span
                          className={
                            f.status === 'failing' ? 'text-danger-foreground' : f.status === 'waiting' ? 'text-muted-foreground' : ''
                          }
                        >
                          {SOCIAL_FEED_STATUS_LABELS[f.status]}
                        </span>
                        <span id={reasonId} className="block text-xs text-muted-foreground">
                          {!ready ? (
                            connectorOf(f.platform)?.reason
                          ) : f.lastError ? (
                            <DataText>{f.lastError}</DataText>
                          ) : f.enabled && f.nextRunAt ? (
                            <>
                              Next check <DataNumber>{when(f.nextRunAt)}</DataNumber>
                            </>
                          ) : null}
                        </span>
                      </td>
                      <td className="p-2">{SCHEDULE_LABELS[f.schedule]}</td>
                      <td className="p-2">
                        <DataNumber>{f.postsStored.toLocaleString()}</DataNumber>
                      </td>
                      <td className="p-2">
                        <DataNumber>{when(f.lastRunAt)}</DataNumber>
                      </td>
                      <td className="p-2">
                        <div className="flex flex-wrap justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            aria-label={`${f.enabled ? 'Switch off' : 'Switch on'} ${f.name}`}
                            aria-describedby={ready ? undefined : reasonId}
                            disabled={(!ready && !f.enabled) || busy}
                            onClick={() => toggle.mutate({ feed: f, enabled: !f.enabled })}
                          >
                            {f.enabled ? 'Switch off' : 'Switch on'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            context={`for ${f.name}`}
                            aria-describedby={ready ? undefined : reasonId}
                            disabled={!ready || busy}
                            onClick={() => run.mutate(f)}
                          >
                            {run.isPending && run.variables?.id === f.id ? 'Checking…' : 'Check now'}
                          </Button>
                          <Button size="sm" variant="ghost" aria-label={`Remove ${f.name}`} onClick={() => askRemove(f)}>
                            Remove
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <LearnMore slug="social-feeds" />
      </section>
    </div>
  );
}

interface FormState {
  name: string;
  platform: SocialFeedPlatform;
  handle: string;
  schedule: SocialFeedSchedule;
  sensitivity: Sensitivity;
  reviewMode: ReviewMode;
}

function FeedForm({
  connectors,
  pending,
  onSubmit,
}: {
  connectors: SocialConnectorSummary[];
  pending: boolean;
  onSubmit: (input: SocialFeedInput) => void;
}) {
  const [form, setForm] = useState<FormState>({
    name: '',
    platform: 'x',
    handle: '',
    schedule: 'daily',
    sensitivity: 'standard',
    reviewMode: 'later',
  });
  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm(f => ({ ...f, [k]: v }));
  const connector = connectors.find(c => c.platform === form.platform);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit({ ...form, name: form.name.trim(), handle: form.handle.trim() });
  };
  return (
    <form onSubmit={submit} className="grid gap-4 rounded-lg border bg-surface p-4 md:grid-cols-2" aria-labelledby="form-title">
      <Field
        guidance={{
          key: 'name',
          label: 'Name',
          required: true,
          placeholder: 'e.g. Ministry of Interior on X',
          help: 'Whose account the feed follows. It also names the Library collection its posts go into (“Social: …”).',
        }}
      >
        {p => <Input {...p} value={form.name} maxLength={80} onChange={e => set('name', e.target.value)} />}
      </Field>
      <Field
        guidance={{
          key: 'platform',
          label: 'Platform',
          help: connector?.reason ?? 'This platform’s connector is ready: the feed can be switched on once added.',
          learnMore: 'social-feeds',
        }}
      >
        {p => (
          <select {...p} className={SELECT} value={form.platform} onChange={e => set('platform', e.target.value as SocialFeedPlatform)}>
            {connectors.map(c => (
              <option key={c.platform} value={c.platform}>
                {c.label}
                {c.state === 'ready' ? '' : ` (${SOCIAL_CONNECTOR_STATE_LABELS[c.state].toLowerCase()})`}
              </option>
            ))}
          </select>
        )}
      </Field>
      <Field
        guidance={{
          key: 'handle',
          label: 'Account',
          required: true,
          placeholder: connector?.handleHint ?? '@name',
          help: `The ${connector?.account ?? 'account'} to follow: ${connector?.handleHint ?? 'its handle or profile address'}.`,
        }}
      >
        {p => <Input {...p} dir="ltr" value={form.handle} maxLength={300} onChange={e => set('handle', e.target.value)} />}
      </Field>
      <Field guidance={{ key: 'schedule', label: 'How often', help: 'How often new posts are read once the feed is on.' }}>
        {p => (
          <select {...p} className={SELECT} value={form.schedule} onChange={e => set('schedule', e.target.value as SocialFeedSchedule)}>
            {SOCIAL_FEED_SCHEDULES.map(s => (
              <option key={s} value={s}>
                {SCHEDULE_LABELS[s]}
              </option>
            ))}
          </select>
        )}
      </Field>
      <Field
        guidance={{
          key: 'sensitivity',
          label: 'Sensitivity',
          help: 'Confidential: the stored posts are confidential and only ever read by Cloudflare-native models.',
          learnMore: 'sensitivity-and-ai-routing',
        }}
      >
        {p => (
          <select {...p} className={SELECT} value={form.sensitivity} onChange={e => set('sensitivity', e.target.value as Sensitivity)}>
            <option value="standard">Standard</option>
            <option value="confidential">Confidential</option>
          </select>
        )}
      </Field>
      <Field
        guidance={{
          key: 'reviewMode',
          label: 'Review',
          help: 'Later: what is extracted from posts is committed as unreviewed and listed in the Review Inbox. Now: each post waits for review.',
          learnMore: 'review-inbox',
        }}
      >
        {p => (
          <select {...p} className={SELECT} value={form.reviewMode} onChange={e => set('reviewMode', e.target.value as ReviewMode)}>
            <option value="later">Later (Review Inbox)</option>
            <option value="now">Now (each post waits for review)</option>
          </select>
        )}
      </Field>
      <div className="md:col-span-2">
        <Button type="submit" disabled={pending}>
          {pending ? 'Saving…' : 'Add feed'}
        </Button>
      </div>
    </form>
  );
}
